import { clearBusinessContextSnapshot, getBusinessContextSnapshot, setBusinessContextSnapshot } from './business-context-cache'
/**
 * H5 鉴权：本地 token 缓存 + RN 桥接拉取/刷新 + 原生主动推送
 * 供 request 拦截器与各业务页复用，新页面无需重复处理 token。
 */
import {
  getAuthTokensFromNative,
  refreshAuthTokensFromNative,
  notifyNativeAuthExpired,
  onAuthTokensUpdated,
  isBridgeAvailable,
  isBridgeAvailableAsync,
  installDeviceStatusBridgeHandler,
  type BridgeAuthTokens,
} from '@/utils/bridge'

export const TOKEN_KEY = 'access_token'
export const REFRESH_TOKEN_KEY = 'refresh_token'

let authGeneration = 0
let bridgeListenerInstalled = false
let authBootstrapPromise: Promise<void> | null = null

export function getLocalAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setLocalTokens(accessToken?: string, refreshToken?: string): void {
  if (accessToken) {
    if (getLocalAccessToken() !== accessToken) authGeneration++
    localStorage.setItem(TOKEN_KEY, accessToken)
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

export function clearLocalTokens(): void {
  authGeneration++
  authBootstrapPromise = null
  clearBusinessContextSnapshot()
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/** URL query 带入 token（浏览器调试 / 旧链路兼容） */
export function saveAccessTokenFromQuery(token?: string): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  }
}

/** 应用启动时从当前 URL 同步 token（早于 router beforeEach，避免首请求无鉴权头） */
export function saveAuthFromLocationSearch(): void {
  if (typeof window === 'undefined') return
  const token = new URLSearchParams(window.location.search).get('token')
  if (token) {
    saveAccessTokenFromQuery(token)
    if (import.meta.env.DEV || import.meta.env.VITE_ENABLE_VCONSOLE === 'true') {
      console.log('[H5Auth] token synced from URL query')
    }
  }
}

function applyTokensFromBridge(tokens: BridgeAuthTokens): void {
  if (tokens.accessToken) {
    setLocalTokens(tokens.accessToken, tokens.refreshToken)
    setBusinessContextSnapshot(tokens.accessToken, tokens.businessContext, tokens.businessContextSupported === true)
  }
}

/** 请求统一复用本次 WebView 的登录握手，避免并发重复拉取或沿用上个账号的 token。 */
export async function ensureAccessToken(): Promise<string | null> {
  // RN 容器不复用未核对账号的 localStorage token；所有并发请求共用一次登录握手。
  if (isBridgeAvailable() || (window as Window & { ReactNativeWebView?: unknown }).ReactNativeWebView) await bootstrapAppAuth()
  return getLocalAccessToken()
}

let refreshingPromise: Promise<string | null> | null = null

/** 401 时通过 RN 刷新 token（并发请求共用一个刷新 Promise） */
export async function refreshAccessTokenViaNative(): Promise<string | null> {
  // 与 ensureAccessToken 同理：异步等待桥注入，避免 Android 8 冷启动误判
  if (!(await isBridgeAvailableAsync())) return null

  if (refreshingPromise) {
    return refreshingPromise
  }

  console.log('[H5Auth] refreshAccessTokenViaNative start', {
    bridge: isBridgeAvailable(),
  })

  refreshingPromise = (async () => {
    const nativeTokens = await refreshAuthTokensFromNative()
    if (!nativeTokens?.accessToken) {
      console.warn('[H5Auth] refreshAccessTokenViaNative failed', {
        bridge: isBridgeAvailable(),
        gotToken: false,
      })
      return null
    }
    applyTokensFromBridge(nativeTokens)
    console.log('[H5Auth] refreshAccessTokenViaNative ok')
    return nativeTokens.accessToken
  })()

  try {
    return await refreshingPromise
  } finally {
    refreshingPromise = null
  }
}

/** 刷新失败：清本地并通知 RN 跳登录 */
export async function handleAuthExpired(): Promise<void> {
  console.warn('[H5Auth] handleAuthExpired', { bridge: isBridgeAvailable() })
  // 浏览器 URL token 调试：保留 query 写入的 token，不通知 RN
  // Android 8 冷启动桥注入可能晚于 401 判定，异步等待确认容器归属
  if (!(await isBridgeAvailableAsync())) return
  clearLocalTokens()
  await notifyNativeAuthExpired()
}

function setupAuthBridgeListener(): void {
  if (bridgeListenerInstalled) return
  bridgeListenerInstalled = true

  onAuthTokensUpdated((tokens) => {
    if (tokens.sessionInvalidated) {
      clearLocalTokens()
      authBootstrapPromise = null
      window.dispatchEvent(new CustomEvent('jellytech:business-context-updated', { detail: { invalidated: true } }))
      return
    }
    const before = getBusinessContextSnapshot(getLocalAccessToken())?.context
    applyTokensFromBridge(tokens)
    const after = getBusinessContextSnapshot(getLocalAccessToken())?.context
    if (before?.userId !== after?.userId || before?.tenantId !== after?.tenantId || before?.businessScene !== after?.businessScene) {
      window.dispatchEvent(new CustomEvent('jellytech:business-context-updated'))
    }
  })
}

/**
 * 应用启动时调用一次：同步 RN token + 监听原生推送更新
 * 建议在 main.ts 中、mount 之前 await
 */
async function runBootstrapAppAuth(): Promise<void> {
  console.log('[H5Diag] bootstrapAppAuth begin', { t: Date.now() })
  // Android 8 冷启动同步读 window.RNBridge 可能尚未注入，必须异步等待
  if (!(await isBridgeAvailableAsync())) {
    console.warn('[H5Diag] bootstrapAppAuth: bridge unavailable, skip', { t: Date.now() })
    if ((window as Window & { ReactNativeWebView?: unknown }).ReactNativeWebView) {
      clearLocalTokens()
      throw new Error('登录通道尚未就绪，请重试')
    }
    return
  }
  console.log('[H5Diag] bootstrapAppAuth: bridge OK, calling getAuthTokensFromNative', { t: Date.now() })

  installDeviceStatusBridgeHandler()
  setupAuthBridgeListener()

  // RN 容器启动时始终向原生拉取一次（会打 [H5Bridge]/[RNBridge] 鉴权日志）
  const generation = authGeneration
  // 首屏 2.5 秒渲染预算不等于登录调用的超时预算。页面已先渲染，
  // 这里保留桥默认的有界等待，允许页面切换/原生存储繁忙时的有效回调返回。
  const nativeTokens = await getAuthTokensFromNative()
  // 登录切换/原生更新先到时，迟到握手不得覆盖新账号或重新恢复已退出账号。
  if (generation !== authGeneration) {
    if (getLocalAccessToken()) return
    throw new Error('登录状态已变更，请重新进入页面')
  }
  console.log('[H5Diag] bootstrapAppAuth: getAuthTokensFromNative returned', {
    hasToken: !!nativeTokens?.accessToken,
    t: Date.now(),
  })
  if (!nativeTokens?.accessToken) {
    clearLocalTokens()
    throw new Error('登录信息同步超时，请重试')
  }
  applyTokensFromBridge(nativeTokens)

  const token = getLocalAccessToken()
  console.log('[H5Auth] bootstrapAppAuth', {
    bridge: isBridgeAvailable(),
    hasToken: !!token,
    syncedFromNative: !!nativeTokens?.accessToken,
  })
}

/** 同一次启动只执行一轮鉴权握手，页面请求可复用该 Promise 等待 token 就绪。 */
export function bootstrapAppAuth(): Promise<void> {
  if (!authBootstrapPromise) {
    const task = runBootstrapAppAuth().catch((error) => {
      if (authBootstrapPromise === task) authBootstrapPromise = null
      throw error
    })
    authBootstrapPromise = task
  }
  return authBootstrapPromise
}
